import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeader, removeToken } from '../../utils/auth';
import { 
  FileText, 
  DollarSign, 
  TrendingUp, 
  AlertTriangle,
  Settings,
  Users,
  Briefcase,
  Building2,
  Package,
  Gift,
  CreditCard,
  Receipt,
  LogOut,
  ArrowLeft,
  UserPlus,
  Calendar,
  UserCircle
} from 'lucide-react';
import '../../styles/admin/ERPDashboardNew.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ERPDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    soumissions: { total: 0, envoyees: 0, acceptees: 0 },
    factures: { total: 0, impayees: 0 },
    revenus: 0,
    aRecevoir: 0,
    stockBas: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [submissionsRes, inventoryRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/submissions`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/erp/inventory`, { headers: getAuthHeader() })
      ]);

      const submissions = submissionsRes.data.submissions || [];
      const inventory = inventoryRes.data || [];

      // Calculate stats
      const soumissionsEnvoyees = submissions.filter(s => s.status === 'En attente').length;
      const soumissionsAcceptees = submissions.filter(s => s.status === 'Acceptée').length;
      
      // Low stock products
      const lowStock = inventory.filter(item => 
        item.actif && item.quantite_stock <= item.seuil_reapprovisionnement
      ).length;

      setStats({
        soumissions: {
          total: submissions.length,
          envoyees: soumissionsEnvoyees,
          acceptees: soumissionsAcceptees
        },
        factures: { total: 0, impayees: 0 }, // À implémenter
        revenus: 0, // À implémenter
        aRecevoir: 0, // À implémenter
        stockBas: lowStock
      });

      setLoading(false);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    removeToken();
    navigate('/admin');
  };

  if (loading) {
    return <div className="erp-loading">Chargement...</div>;
  }

  return (
    <div className="erp-dashboard-page">
      {/* Header */}
      <div className="erp-dashboard-header">
        <div className="erp-dashboard-header-content">
          <div className="header-left">
            <div className="logo-section">
              <span className="logo-icon">🌲</span>
              <h1>Arborescence</h1>
            </div>
            <p className="tagline">Gestion complète de votre entreprise</p>
          </div>
          <div className="header-right">
            <Link to="/" className="btn-header-secondary">
              <ArrowLeft size={18} />
              Retour aux soumissions
            </Link>
            <Link to="/admin/erp/profile" className="btn-header-secondary">
              <UserCircle size={18} />
              Mon Profil
            </Link>
            <button className="btn-header-secondary" onClick={handleLogout}>
              <LogOut size={18} />
              Déconnexion
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="erp-dashboard-content">
        {/* KPIs */}
        <div className="kpi-grid">
          <div className="kpi-card kpi-soumissions">
            <div className="kpi-icon">
              <FileText size={32} />
            </div>
            <div className="kpi-value">{stats.soumissions.total}</div>
            <div className="kpi-label">SOUMISSIONS</div>
            <div className="kpi-details">
              {stats.soumissions.envoyees} envoyées · {stats.soumissions.acceptees} acceptées
            </div>
          </div>

          <div className="kpi-card kpi-factures">
            <div className="kpi-icon">
              <DollarSign size={32} />
            </div>
            <div className="kpi-value">{stats.factures.total}</div>
            <div className="kpi-label">FACTURES</div>
            <div className="kpi-details kpi-warning">
              {stats.factures.impayees} impayées
            </div>
          </div>

          <div className="kpi-card kpi-revenus">
            <div className="kpi-icon">
              <TrendingUp size={32} />
            </div>
            <div className="kpi-value">{stats.revenus.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</div>
            <div className="kpi-label">REVENUS TOTAUX</div>
          </div>

          <div className="kpi-card kpi-recevoir">
            <div className="kpi-icon">
              <AlertTriangle size={32} />
            </div>
            <div className="kpi-value">{stats.aRecevoir.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</div>
            <div className="kpi-label">À RECEVOIR</div>
            <div className="kpi-details kpi-warning">
              {stats.stockBas} produit(s) en stock bas
            </div>
          </div>
        </div>

        {/* Navigation Cards */}
        <div className="nav-cards-grid">
          <Link to="/admin/production/dispatch" className="nav-card nav-card-highlight">
            <div className="nav-card-icon">
              <Calendar size={40} />
            </div>
            <h3>🚀 Production & Planning</h3>
            <p>Gérer les interventions terrain</p>
          </Link>

          <Link to="/admin/leads" className="nav-card nav-card-highlight">
            <div className="nav-card-icon">
              <UserPlus size={40} />
            </div>
            <h3>🎯 Leads</h3>
            <p>Gérer les prospects et opportunités</p>
          </Link>

          <Link to="/admin/erp/clients" className="nav-card">
            <div className="nav-card-icon">
              <Users size={40} />
            </div>
            <h3>Clients</h3>
            <p>Gérer les fiches clients</p>
          </Link>

          <Link to="/admin/erp/services" className="nav-card">
            <div className="nav-card-icon">
              <Briefcase size={40} />
            </div>
            <h3>Services</h3>
            <p>Gérer les services et tarifications</p>
          </Link>

          <Link to="/admin/erp/departments" className="nav-card">
            <div className="nav-card-icon">
              <Building2 size={40} />
            </div>
            <h3>Départements</h3>
            <p>Organiser les services par département</p>
          </Link>

          <Link to="/admin/erp/inventory" className="nav-card">
            <div className="nav-card-icon">
              <Package size={40} />
              {stats.stockBas > 0 && (
                <span className="nav-badge">{stats.stockBas}</span>
              )}
            </div>
            <h3>Inventaire</h3>
            <p>Gérer les produits et stock</p>
          </Link>

          <Link to="/admin/erp/packages" className="nav-card">
            <div className="nav-card-icon">
              <Gift size={40} />
            </div>
            <h3>Forfaits</h3>
            <p>Créer des packages de services</p>
          </Link>

          <Link to="/admin/erp/payment-modalities" className="nav-card">
            <div className="nav-card-icon">
              <CreditCard size={40} />
            </div>
            <h3>Modalités de paiement</h3>
            <p>Configurer les options de paiement</p>
          </Link>

          <Link to="/admin/erp/invoices" className="nav-card">
            <div className="nav-card-icon">
              <Receipt size={40} />
            </div>
            <h3>Facturation</h3>
            <p>Gérer les factures et paiements</p>
            <div className="nav-card-preview">
              <div className="preview-mini-invoice">
                <div className="preview-header"></div>
                <div className="preview-line"></div>
                <div className="preview-line"></div>
                <div className="preview-line short"></div>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ERPDashboard;
